/**
 * Profile ↔ Application Field Mapping Registry
 * ============================================================================
 *
 * Binds application form field keys (from `auditions.application_structure`)
 * to columns on the three dancer profile tables (`profiles` /
 * `professional_profiles` / `artist_profiles`).
 *
 * Why this exists
 * ---------------
 * The legacy Meteor app prefilled audition applications from the dancer's
 * profile AND backfilled the profile with any changes made in the application
 * form. This registry is how we replicate that behaviour in Ballee without
 * hardcoding profile-specific logic in every form.
 *
 * The audition form system is intentionally generic (fields are defined
 * per-audition in `auditions.application_structure`), so we can't embed profile
 * knowledge there. Instead, form fields identified by one of the keys below
 * participate in prefill/backfill. Unknown keys are application-private — the
 * value sits on `audition_applications.fields` and never touches the profile.
 *
 * How to add a new mapping
 * ------------------------
 *  1. Add the field to `PROFILE_FIELDS` in
 *     `packages/features/auditions/src/components/application-form/application-form-builder.tsx`
 *     so it shows up in the form builder palette.
 *  2. Add an entry here with `table`, `column`, `mergeStrategy`, and (if the
 *     form shape differs from the DB shape) `transform` + `reverse`.
 *  3. Extend the `PROFILE_FIELD_MAP_KEYS` test fixture.
 *
 * Merge strategies
 * ----------------
 *  - `fill-if-null`   — only write if the profile column is currently null.
 *                       Never clobber existing profile data.  (default for
 *                       scalars)
 *  - `union`          — array-valued columns: take the set union of existing
 *                       + submitted. Applications can *add* to a list but
 *                       never remove.  (default for arrays)
 *  - `overwrite`      — write unconditionally if the submitted value differs.
 *                       Use sparingly — only for fields where the application
 *                       form is always the most accurate source.
 *
 * Conflict rules (codified)
 * -------------------------
 *  1. null → value        : backfill.
 *  2. value → null        : ignore (never clear a profile field via an app).
 *  3. array field         : union (no removal via an application).
 *  4. non-null scalar     : respect existing value unless strategy=overwrite.
 *
 * See also
 * --------
 *  - `profile_backfill_audit` table — every write via this registry is logged.
 *  - `apps/web/app/home/(user)/auditions/[slug]/_components/apply-dialog.tsx`
 *    — prefill entry point.
 *  - `ApplicationService.create` / `submit` — backfill entry point.
 *  - Plan: `~/.claude/plans/we-should-share-sharded-biscuit.md` (Phase 1).
 */

import { toPhoneE164 } from '@kit/artists/lib/phone-e164';

import type { ApplicationStructure } from '../types/audition.types';

// ==================== Types ====================

/**
 * Tables a field can bind to. A binding targets exactly one of these.
 */
export type ProfileTable =
  | 'profiles'
  | 'professional_profiles'
  | 'artist_profiles';

export type MergeStrategy = 'overwrite' | 'union' | 'fill-if-null';

/**
 * Binding between an application form field key and a profile column.
 *
 *  - `transform`  : form-value → db-value. Used when the form shape differs
 *                   from the DB shape (e.g. form scalar → DB array).
 *  - `reverse`    : db-value → form-value. Used for prefill.
 *
 * Both transforms receive `unknown` because field values come from generic
 * JSON. Return `undefined` from `transform` to signal "skip this write".
 */
export interface ProfileFieldBinding {
  table: ProfileTable;
  column: string;
  mergeStrategy: MergeStrategy;
  transform?: (formValue: unknown) => unknown;
  reverse?: (dbValue: unknown) => unknown;
}

/**
 * Snapshot of the three profile rows for a single user. Passed to prefill
 * and backfill helpers so they don't need to know how to load the profile.
 *
 * The professional_profiles.id and artist_profiles.id share the same value
 * (artist_profiles references professional_profiles). profiles.id is the
 * auth.users.id — different value.
 */
export interface ProfileSnapshot {
  profile: Record<string, unknown> | null;
  professionalProfile: Record<string, unknown> | null;
  dancerProfile: Record<string, unknown> | null;
  userEmail?: string | null;
}

/**
 * One resolved backfill operation. Produced by `computeBackfillOperations`,
 * consumed by `applyBackfill`.
 */
export interface BackfillOperation {
  table: ProfileTable;
  column: string;
  sourceFieldKey: string;
  mergeStrategy: MergeStrategy;
  oldValue: unknown;
  newValue: unknown;
}

// ==================== Helpers ====================
// scalarOrArrayToArray + firstOfArrayOrUndefined helpers were removed in
// Phase 4 of the Meteor migration: the only consumers were the headshot
// and resume mappings on professional_profiles, both of which moved to
// artist_media (per-row, not per-column array).

// ==================== Registry ====================

/**
 * Maps application form field keys → profile column bindings.
 *
 * Keys NOT in this map are application-private: their values live on
 * `audition_applications.fields` and never touch the dancer's profile.
 *
 * Fields explicitly excluded from the registry even though they appear in
 * the palette:
 *  - `contactEmail`   — email lives on `auth.users`, not on a profile
 *                       column. We can prefill from `user.email` directly;
 *                       we do not backfill (users can't update auth.users
 *                       via RLS).
 *  - `videoLink`      — per-application URL; belongs on the application row,
 *                       not the profile. `professional_profiles.video_media_gallery`
 *                       is a different concept (persistent media bucket) and
 *                       will be handled by a future mapping when the uploader
 *                       path is finalised.
 */
export const PROFILE_FIELD_MAP: Readonly<Record<string, ProfileFieldBinding>> =
  {
    firstname: {
      table: 'profiles',
      column: 'first_name',
      mergeStrategy: 'fill-if-null',
    },
    lastname: {
      table: 'profiles',
      column: 'last_name',
      mergeStrategy: 'fill-if-null',
    },
    contactPhone: {
      table: 'profiles',
      column: 'phone_number',
      mergeStrategy: 'fill-if-null',
      // BAL-1073: only an E.164-convertible number reaches the profile. A
      // national one stays on the application and is not copied.
      transform: (value) =>
        typeof value === 'string'
          ? (toPhoneE164(value) ?? undefined)
          : undefined,
    },
    birthdate: {
      table: 'profiles',
      column: 'date_of_birth',
      mergeStrategy: 'fill-if-null',
    },
    gender: {
      table: 'profiles',
      column: 'gender',
      mergeStrategy: 'fill-if-null',
    },
    city: {
      table: 'profiles',
      column: 'city',
      mergeStrategy: 'fill-if-null',
    },
    height: {
      table: 'artist_profiles',
      column: 'height_cm',
      mergeStrategy: 'fill-if-null',
    },
    nationalities: {
      table: 'profiles',
      column: 'nationalities',
      mergeStrategy: 'union',
    },
    residenceCountry: {
      table: 'profiles',
      column: 'country_of_residence',
      mergeStrategy: 'fill-if-null',
    },
    languages: {
      table: 'profiles',
      column: 'languages',
      mergeStrategy: 'union',
    },
    instagramUsername: {
      table: 'professional_profiles',
      column: 'instagram_handle',
      mergeStrategy: 'fill-if-null',
    },
    tiktokUsername: {
      table: 'professional_profiles',
      column: 'tiktok_handle',
      mergeStrategy: 'fill-if-null',
    },
    // Headshot + resume mappings removed (Phase 4 of Meteor migration):
    // those lived on professional_profiles.{head_shot_media,resume_media}
    // text[] columns which were dropped. Both now live exclusively in
    // artist_media (media_type='headshot' and media_type='document'
    // respectively). Application forms that need to attach a fresh
    // headshot/resume go through the DancerMediaService directly, not
    // through this profile field-mapping registry.

    // `currentAffiliation` (and `danceEducation` before it) were retired in
    // 20260426142200 / 20260426105624. Audition forms that need "currently at"
    // should query the `experiences` table directly: the rich ExperienceStep
    // card writes those rows with `current = true`.
    eyeColor: {
      table: 'artist_profiles',
      column: 'eye_color',
      mergeStrategy: 'fill-if-null',
    },
    hairColor: {
      table: 'artist_profiles',
      column: 'hair_color',
      mergeStrategy: 'fill-if-null',
    },
    skinTone: {
      table: 'artist_profiles',
      column: 'skin_tone',
      mergeStrategy: 'fill-if-null',
    },
    weight: {
      table: 'artist_profiles',
      column: 'weight_kg',
      mergeStrategy: 'fill-if-null',
    },
    // Bio asks for a paragraph. Most dancers write theirs once and reuse;
    // backfilling lets a one-time audition answer populate the profile so
    // the next audition prefills it. Profile keeps prior bio if already
    // set (fill-if-null), preventing audition-by-audition drift.
    bio: {
      table: 'professional_profiles',
      column: 'bio',
      mergeStrategy: 'fill-if-null',
    },
    yearsOfExperience: {
      table: 'professional_profiles',
      column: 'years_of_experience',
      mergeStrategy: 'fill-if-null',
    },
    // dance_styles is dance_style_type[] — union mirrors `nationalities`
    // and `languages`. Applications can broaden the dancer's declared
    // style set but never narrow it.
    danceStyles: {
      table: 'professional_profiles',
      column: 'dance_styles',
      mergeStrategy: 'union',
    },
    portfolioUrl: {
      table: 'professional_profiles',
      column: 'portfolio_url',
      mergeStrategy: 'fill-if-null',
    },
    stageName: {
      table: 'professional_profiles',
      column: 'stage_name',
      mergeStrategy: 'fill-if-null',
    },
    website: {
      table: 'professional_profiles',
      column: 'website',
      mergeStrategy: 'fill-if-null',
    },
  };

// ==================== Prefill ====================

/**
 * Look up the current profile value for a given form field key.
 * Applies the binding's `reverse` transform if present.
 *
 * Returns `undefined` when:
 *  - the key isn't in the registry
 *  - the relevant profile row isn't loaded
 *  - the column is null
 *
 * Special-case: `contactEmail` returns `userEmail` from the snapshot.
 */
export function getProfileValueForField(
  fieldKey: string,
  snapshot: ProfileSnapshot,
): unknown {
  if (fieldKey === 'contactEmail') {
    return snapshot.userEmail ?? undefined;
  }

  const binding = PROFILE_FIELD_MAP[fieldKey];
  if (!binding) return undefined;

  const row = snapshotRow(snapshot, binding.table);
  if (!row) return undefined;

  const raw = row[binding.column];
  if (raw === null || raw === undefined) return undefined;

  return binding.reverse ? binding.reverse(raw) : raw;
}

/**
 * Build react-hook-form defaultValues for an application form by walking
 * `applicationStructure.fields` and resolving each profile-backed key.
 *
 * Unknown keys are omitted (react-hook-form treats undefined as "empty").
 */
export function buildApplicationDefaults(
  applicationStructure: ApplicationStructure | null,
  snapshot: ProfileSnapshot,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  if (!applicationStructure || !Array.isArray(applicationStructure.fields)) {
    return defaults;
  }

  for (const field of applicationStructure.fields) {
    if (field.type === 'heading' || field.type === 'paragraph') continue;
    const value = getProfileValueForField(field.key, snapshot);
    if (value !== undefined) {
      defaults[field.key] = value;
    }
  }

  return defaults;
}

// ==================== Backfill ====================

/**
 * Compute the set of backfill writes implied by a submitted application.
 *
 * Walks `applicationStructure.fields`, looks each key up in the registry,
 * applies the binding's `transform`, and compares the resulting value to
 * the current profile value per `mergeStrategy`.
 *
 * Emits zero operations when the submitted value is null/undefined or
 * matches the profile already.
 */
export function computeBackfillOperations(
  applicationStructure: ApplicationStructure | null,
  submittedFields: Record<string, unknown>,
  snapshot: ProfileSnapshot,
): BackfillOperation[] {
  const ops: BackfillOperation[] = [];

  if (!applicationStructure || !Array.isArray(applicationStructure.fields)) {
    return ops;
  }

  for (const field of applicationStructure.fields) {
    if (field.type === 'heading' || field.type === 'paragraph') continue;

    const binding = PROFILE_FIELD_MAP[field.key];
    if (!binding) continue;

    const rawSubmitted = submittedFields[field.key];
    if (rawSubmitted === undefined || rawSubmitted === null) continue;
    if (typeof rawSubmitted === 'string' && rawSubmitted.length === 0) continue;

    const transformed = binding.transform
      ? binding.transform(rawSubmitted)
      : rawSubmitted;
    if (transformed === undefined) continue;

    const row = snapshotRow(snapshot, binding.table);
    const oldValue = row ? row[binding.column] : null;

    const newValue = applyMergeStrategy(
      binding.mergeStrategy,
      oldValue,
      transformed,
    );
    if (newValue === undefined) continue; // no-op per strategy

    // Deep equality check — don't enqueue identical writes.
    if (isDeepEqual(oldValue, newValue)) continue;

    ops.push({
      table: binding.table,
      column: binding.column,
      sourceFieldKey: field.key,
      mergeStrategy: binding.mergeStrategy,
      oldValue: oldValue ?? null,
      newValue,
    });
  }

  return ops;
}

// ==================== Internals ====================

function snapshotRow(
  snapshot: ProfileSnapshot,
  table: ProfileTable,
): Record<string, unknown> | null {
  switch (table) {
    case 'profiles':
      return snapshot.profile;
    case 'professional_profiles':
      return snapshot.professionalProfile;
    case 'artist_profiles':
      return snapshot.dancerProfile;
  }
}

/**
 * Apply the merge strategy. Returns `undefined` when the strategy implies
 * "no write" given the current values.
 */
function applyMergeStrategy(
  strategy: MergeStrategy,
  oldValue: unknown,
  newValue: unknown,
): unknown {
  if (strategy === 'overwrite') {
    return newValue;
  }

  if (strategy === 'fill-if-null') {
    if (oldValue === null || oldValue === undefined) return newValue;
    return undefined;
  }

  // union — only valid for array-valued columns.
  const existing = Array.isArray(oldValue) ? oldValue : [];
  const incoming = Array.isArray(newValue) ? newValue : [newValue];
  const merged: unknown[] = [...existing];
  for (const item of incoming) {
    if (item === null || item === undefined) continue;
    if (!merged.some((e) => isDeepEqual(e, item))) {
      merged.push(item);
    }
  }
  return merged;
}

/**
 * Value equality for JSON-shaped data. Arrays are order-sensitive by design —
 * we want `['ballet'] !== ['contemporary']` without shuffling either side.
 */
function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isDeepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object).sort();
    const kb = Object.keys(b as object).sort();
    if (ka.length !== kb.length) return false;
    for (let i = 0; i < ka.length; i++) {
      const keyA = ka[i];
      const keyB = kb[i];
      if (keyA === undefined || keyB === undefined) return false;
      if (keyA !== keyB) return false;
      if (
        !isDeepEqual(
          (a as Record<string, unknown>)[keyA],
          (b as Record<string, unknown>)[keyB],
        )
      ) {
        return false;
      }
    }
    return true;
  }
  return false;
}
